import {Emitter} from '@wonderlandengine/api';
import {Justify, Align, PositionType} from '@wonderlandengine/react-ui';
import {
    PanelProps,
    Container,
    Panel9Slice,
    MaterialContext,
    Panel,
    Row,
    Image,
    Text,
    Column,
} from '@wonderlandengine/react-ui/components';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {UIColorTextured, UIColorTexturedAlpha} from '../../types/materials.js';
import {NotifyPropertyChanged} from '../../utils/NotifyPropertyChanged.js';
import {ColoredImage} from '../components/colored-image.js';
import {MalUI} from '../components/mal-ui.js';
import {MenuButton} from '../components/menu-button.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {
    useRotBMaterialContext,
    RotBMaterialContextValue,
} from '../utils/rotb-material-context.js';
import {insertNewlines, parseColor} from '../utils/utils.js';
import {UIModel} from '../../models/UIModel.js';

type possibleProps = 'isVisible' | 'text' | 'acceptButtonName' | 'cancelButtonName';

const DIALOG_Z = 0.0;

export class ConfirmationDialogController implements NotifyPropertyChanged {
    private static _instance: ConfirmationDialogController;

    private _isVisible = false;
    get isVisible() {
        return this._isVisible;
    }
    private set isVisible(value: boolean) {
        if (value === this._isVisible) return;
        this._isVisible = value;
        this.notifyPropertyChanged('isVisible');
    }

    private _text: string = '';
    get text() {
        return this._text;
    }
    private set text(value: string) {
        if (value === this._text) return;
        this._text = value;
        this.notifyPropertyChanged('text');
    }

    private _acceptButtonName: string = '';
    get acceptButtonName() {
        return this._acceptButtonName;
    }
    private set acceptButtonName(value: string) {
        if (value === this._acceptButtonName) return;
        this._acceptButtonName = value;
        this.notifyPropertyChanged('acceptButtonName');
    }

    private _cancelButtonName: string = undefined;
    get cancelButtonName() {
        return this._cancelButtonName;
    }
    private set cancelButtonName(value: string) {
        if (value === this._cancelButtonName) return;
        this._cancelButtonName = value;
        this.notifyPropertyChanged('cancelButtonName');
    }

    private _resultCallback: (action: boolean) => void = () => {};

    static get instance(): ConfirmationDialogController {
        if (!ConfirmationDialogController._instance) {
            ConfirmationDialogController._instance = new ConfirmationDialogController();
        }
        return ConfirmationDialogController._instance;
    }

    private constructor() {}
    propertyChanged: Emitter<[possibleProps]> = new Emitter();

    notifyPropertyChanged(propertyName: possibleProps): void {
        this.propertyChanged.notify(propertyName);
    }

    hide(action: boolean) {
        this.isVisible = false;
        if (this._resultCallback) {
            this._resultCallback(action);
        }
        UIModel.instance.setPopupActive(false);
    }

    displayDialog(text: string, acceptButtonName: string): void;
    displayDialog(
        text: string,
        acceptButtonName: string,
        resultCallback: (action: boolean) => void
    ): void;
    displayDialog(
        text: string,
        acceptButtonName: string,
        cancelButtonName: string,
        resultCallback: (action: boolean) => void
    ): void;

    // Implementation
    displayDialog(
        text: string,
        acceptButtonName: string,
        cancelOrCallback?: string | ((action: boolean) => void),
        resultCallback?: (action: boolean) => void
    ): void {
        this.text = text;
        this.acceptButtonName = acceptButtonName;

        if (typeof cancelOrCallback === 'string') {
            this.cancelButtonName = cancelOrCallback;
            this._resultCallback = resultCallback;
        } else {
            this.cancelButtonName = undefined;
            this._resultCallback = cancelOrCallback;
        }

        this.isVisible = true;
        UIModel.instance.setPopupActive(true);
    }
}
function useConfirmationDialogController(): [
    boolean,
    string,
    string,
    string,
    () => void,
    () => void,
] {
    const controller = useRef(ConfirmationDialogController.instance);
    const [isVisible, setIsVisible] = useState(false);
    const [text, setText] = useState('');
    const [acceptButtonText, setAcceptButtonText] = useState('');
    const [cancelButtonText, setCancelButtonText] = useState(undefined);

    useEffect(() => {
        if (!controller.current) return;
        const propertyChanged = (property: possibleProps) => {
            if (property === 'text') {
                setText(controller.current.text);
            }
            if (property === 'acceptButtonName') {
                setAcceptButtonText(controller.current.acceptButtonName);
            }
            if (property === 'cancelButtonName') {
                setCancelButtonText(controller.current.cancelButtonName);
            }
            if (property === 'isVisible') {
                setIsVisible(controller.current.isVisible);
            }
        };

        controller.current.propertyChanged.add(propertyChanged);

        return () => {
            controller.current.propertyChanged.remove(propertyChanged);
        };
    }, []);

    const onAcceptClick = () => {
        if (controller.current) {
            controller.current.hide(true);
        }
    };

    const onCancelClick = () => {
        if (controller.current) {
            controller.current.hide(false);
        }
    };

    return [
        isVisible,
        text,
        acceptButtonText,
        cancelButtonText,
        onAcceptClick,
        onCancelClick,
    ];
}

export const ConfirmationDialog = (props: React.PropsWithChildren<PanelProps> & {}) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    let material = rotbMats?.windowMaterial;

    const bm = useMemo(() => material!.clone(), []);
    (bm as UIColorTextured).setColor(parseColor(colorSwatch.Window));

    const darkBackgroundMaterial = useMemo(() => {
        const m = rotbMats.gradientMaterial!.clone() as UIColorTexturedAlpha;
        m.setColor(parseColor('#000000A0'));
        m.setFlatTexture(UiTexturesManager.instance.whiteSquare);
        return m;
    }, []);

    const shadowMaterial = rotbMats?.windowShadowMaterial;
    const gradientMaterial = rotbMats?.gradientMaterial;
    (gradientMaterial as any).setColor(parseColor(colorSwatch.WindowGradient));

    const [
        isVisible,
        text,
        acceptButtonText,
        cancelButtonText,
        onAcceptClick,
        onCancelClick,
    ] = useConfirmationDialogController();

    return (
        <>
            {isVisible && (
                <Panel
                    z={DIALOG_Z}
                    rounding={0}
                    width="100%"
                    height="100%"
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                    material={darkBackgroundMaterial}
                >
                    {/* Add something to darken entire screen */}
                    {/* <Panel9Slice
                        z={0.02}
                        width={500}
                        height={520}
                        borderSize={5}
                        material={shadowMaterial}
                        borderTextureSize={9 / 64}
                    > */}
                    <Panel9Slice
                        z={DIALOG_Z}
                        left={-4}
                        top={-4}
                        width={500}
                        height={520}
                        borderSize={5}
                        material={bm}
                        borderTextureSize={9 / 64}
                        padding={5}
                    >
                        <Panel
                            z={DIALOG_Z}
                            width="100%"
                            height="100%"
                            rounding={0}
                            material={gradientMaterial}
                        >
                            <Row left={138} top={20} z={DIALOG_Z} height={224}>
                                {/* Portrait */}
                                <Container>
                                    <ColoredImage
                                        position={PositionType.Absolute}
                                        src={UiTexturesManager.instance.whiteCircle}
                                        color={colorSwatch.MalPortraitBackground}
                                        width={224}
                                        height={224}
                                    ></ColoredImage>
                                    <ColoredImage
                                        position={PositionType.Absolute}
                                        src={UiTexturesManager.instance.whiteRing}
                                        color={colorSwatch.SubTitleBackground}
                                        width={224}
                                        height={224}
                                    ></ColoredImage>
                                    <MalUI
                                        top={28}
                                        position={PositionType.Absolute}
                                        width={224}
                                        height={224}
                                    ></MalUI>
                                </Container>
                            </Row>
                            <Column
                                z={DIALOG_Z}
                                marginTop={50}
                                gap={10}
                                width="100%"
                                justifyContent={Justify.SpaceAround}
                                alignItems={Align.Center}
                            >
                                <Panel
                                    z={DIALOG_Z}
                                    width={400}
                                    height={110}
                                    rounding={0}
                                    backgroundColor={colorSwatch.SubTitleHighBackground}
                                    alignItems={Align.Center}
                                    justifyContent={Justify.Center}
                                >
                                    <Text
                                        width={350}
                                        marginTop={5}
                                        z={DIALOG_Z}
                                        textAlign="center"
                                        material={rotbMats.regularTextMaterial}
                                        color={colorSwatch.DescriptionText}
                                        fontSize={26}
                                        textWrap="soft"
                                    >
                                        {text}
                                    </Text>
                                </Panel>
                                {cancelButtonText ? (
                                    <Row>
                                        <MenuButton
                                            z={DIALOG_Z}
                                            key="CancelButtonCenter"
                                            width={220}
                                            height={80}
                                            margin={6}
                                            color={colorSwatch.CancelButton}
                                            fontSize={26}
                                            onClick={onCancelClick}
                                        >
                                            {cancelButtonText}
                                        </MenuButton>
                                        <MenuButton
                                            z={DIALOG_Z}
                                            key="AcceptButtonCenter"
                                            width={220}
                                            height={80}
                                            margin={6}
                                            color={colorSwatch.AcceptButton}
                                            fontSize={26}
                                            onClick={onAcceptClick}
                                        >
                                            {acceptButtonText}
                                        </MenuButton>
                                    </Row>
                                ) : (
                                    <MenuButton
                                        z={DIALOG_Z}
                                        key="AcceptButtonCenter"
                                        width={220}
                                        height={80}
                                        margin={6}
                                        color={colorSwatch.AcceptButton}
                                        fontSize={26}
                                        onClick={onAcceptClick}
                                    >
                                        {acceptButtonText}
                                    </MenuButton>
                                )}
                            </Column>
                        </Panel>
                    </Panel9Slice>
                    {/* </Panel9Slice> */}
                </Panel>
            )}
        </>
    );
};
